
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import Sidebar from '../components/Sidebar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useModulesWithProgress } from '@/hooks/useModulesWithProgress'

interface LearnModule {
  id: string
  title: string
  description: string
  category: string
  coins: number
  image_url: string
  completed: boolean
  order_index: number
}

const Learn = () => {
  const [activeCategory, setActiveCategory] = useState('Wealth')
  const { modules: learnModules, loading } = useModulesWithProgress(activeCategory)

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Wealth':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'Self':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'Social':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getButtonColor = (category: string) => {
    switch (category) {
      case 'Wealth':
        return 'bg-green-500 hover:bg-green-600 text-white'
      case 'Self':
        return 'bg-blue-500 hover:bg-blue-600 text-white'
      case 'Social':
        return 'bg-orange-500 hover:bg-orange-600 text-white'
      default:
        return 'bg-gray-500 hover:bg-gray-600 text-white'
    }
  }

  const categories = ['Wealth', 'Self', 'Social']

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Learn</h1>
            <p className="text-gray-600">Expand your knowledge with interactive modules and earn coins</p>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 mb-8">
            {categories.map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? "default" : "outline"}
                className={activeCategory === category ? getButtonColor(category) : "text-gray-600 hover:text-gray-900"}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading modules...</p>
            </div>
          ) : (
            <>
              {/* Learn Modules Grid */}
              {learnModules.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {learnModules.map((module) => (
                    <Link key={module.id} to={`/learn/module/${module.id}`}>
                      <Card className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all overflow-hidden cursor-pointer group">
                        <div className="relative">
                          <div className="h-48 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center group-hover:from-indigo-200 group-hover:to-purple-200 transition-colors">
                            {module.image_url ? (
                              <img 
                                src={module.image_url} 
                                alt={module.title}
                                className="w-24 h-24 object-cover rounded-lg shadow-sm"
                              />
                            ) : (
                              <div className="w-24 h-24 bg-white rounded-lg shadow-sm flex items-center justify-center">
                                <div className={`w-16 h-16 rounded-lg ${
                                  module.category === 'Wealth' ? 'bg-gradient-to-br from-green-400 to-green-600' :
                                  module.category === 'Self' ? 'bg-gradient-to-br from-blue-400 to-blue-600' :
                                  module.category === 'Social' ? 'bg-gradient-to-br from-orange-400 to-orange-600' :
                                  'bg-gradient-to-br from-gray-400 to-gray-600'
                                }`}></div>
                              </div>
                            )}
                          </div>
                          
                          {/* Category Badge */}
                          <Badge className={`absolute top-3 left-3 ${getCategoryColor(module.category)}`}>
                            {module.category}
                          </Badge>
                          
                          {/* Coins Badge */}
                          <div className="absolute top-3 right-3 flex items-center gap-1 bg-white rounded-full px-2 py-1 shadow-sm">
                            <div className="w-4 h-4 bg-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-xs text-white font-bold">₿</span>
                            </div>
                            <span className="text-sm font-medium text-purple-600">{module.coins} COINS</span>
                          </div>
                        </div>

                        <CardContent className="p-6">
                          <h3 className="font-semibold text-gray-900 mb-2 text-lg group-hover:text-purple-600 transition-colors">{module.title}</h3>
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3">{module.description}</p>
                          
                          <Button 
                            className={`w-full ${
                              module.completed 
                                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                : 'bg-purple-600 hover:bg-purple-700 text-white'
                            }`}
                            variant={module.completed ? 'outline' : 'default'}
                          >
                            {module.completed ? 'Completed ✓' : 'Start Learning'}
                          </Button>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">No modules available for {activeCategory} category</p>
                  <p className="text-sm text-gray-500 mt-2">Check back later for new content!</p>
                </div>
              )}

              {/* Load More */}
              {learnModules.length > 0 && (
                <div className="text-center mt-12">
                  <Button variant="outline" className="px-8 py-2">
                    Load More Modules
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  )
}

export default Learn
